import { useParams } from 'react-router-dom';
import {
  AuditOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  IdcardOutlined,
  PictureOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { Bento, DataTable, HanMucHoacCauGoc, PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import {
  KET_LUAN_HANG_MUC,
  MUC_RUI_RO,
  MUC_VAN_DE,
  MO_HANG_MUC,
  QUYEN,
  QUYET_DINH,
  TEN_HANG_MUC,
  TEN_VAI_TRO_ANH,
  TEN_YEU_TO,
  THU_TU_HANG_MUC,
  THU_TU_YEU_TO,
  TRANG_THAI_PHIEN,
  VAI_TRO_ANH,
} from '../constants';
import { KetQuaThamDinhView, KhoiRaQuyetDinh } from '../components/KhoiThamDinh';
import { useAnhPhien, useNhatKy, usePhien, useThongTinGiayTo } from '../hooks/useEkyc';
import { coDiem, khoangCachNguong, thieuGhiSo } from '../nghiep-vu/ekyc';
import type { DongNhatKy, KetQuaHangMuc, MaHangMuc, PhienChiTiet, ThongTinGiayTo } from '../types';

/**
 * Chi tiết hồ sơ định danh (`/ekyc/ho-so/:id`) — **màn thẩm định**.
 *
 * Đây là nơi một người ra quyết định về một người khác, nên màn này được dựng quanh đúng một câu
 * hỏi: *người duyệt có đọc ra được **vì sao** hệ thống kết luận như vậy không, và chỗ nào hệ thống
 * **chưa biết***?
 *
 * Bốn quyết định thiết kế, mỗi cái gắn với một sự cố có thật trong bộ bàn giao:
 *
 * 1. **Ảnh và dữ liệu cá nhân mở theo yêu cầu, không tải sẵn.** Mỗi lần mở ghi một dòng nhật ký
 *    kèm tên người xem (`02` §7.1) — đó là thứ duy nhất phân biệt truy cập hợp pháp với tò mò về
 *    dữ liệu người khác. Tải sẵn là biến mọi lần mở trang thành một lần "đã xem ảnh mặt người".
 * 2. **Gác bằng `useActionPermission` trong trang, không gác bằng route.** `EKYC_REVIEW` là module
 *    riêng (`06` §1.8): cùng một trang, người có quyền xem hồ sơ và người được xem ảnh mặt thật là
 *    hai vai khác nhau. Thiếu quyền thì **ẩn** mục, không hiện mờ (ADR-UI-0005).
 * 3. **"Chưa vào sổ định danh" là banner thường trú, không phải toast** (`07` §23.1): ai mở lại hồ
 *    sơ một tháng sau cũng phải thấy.
 * 4. **Hạng mục `ERROR`/`SKIPPED` không hiện điểm số.** Cả hai mang `score = 0` (`04` §1.3); vẽ
 *    "0,00" ở đó là nói rằng đã kiểm và trượt sạch, trong khi chưa có phép kiểm nào chạy.
 */
export function ChiTietHoSoDinhDanhView() {
  const { t } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const { id = '' } = useParams<{ id: string }>();
  const quyen = useModulePermissions(QUYEN);

  const q = usePhien(id);
  const nhatKy = useNhatKy(id);
  const giayTo = useThongTinGiayTo(id);
  const anh = useAnhPhien(id);

  const p = q.data;
  const tieuDe = t('ekyc.chiTietTieuDe', { ma: p?.referenceId || id });

  if (q.isLoading) {
    return (
      <div className="flex h-full flex-col bg-subtle">
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (q.isError || !p) {
    return (
      <div className="flex h-full flex-col bg-subtle">
        <div className="flex-1 overflow-y-auto p-4">
          <Empty
            icon={FileSearchOutlined}
            title={t('ekyc.khongTimThay')}
            description={t('ekyc.khongTimThayMo')}
            action={
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  const tongTrangThai = TRANG_THAI_PHIEN[p.status];
  const canhBaoSo = thieuGhiSo(p.status, p.registryEnrolled);

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.dinhDanhEkyc') },
            { label: t('ekyc.hoSoTieuDe'), onClick: () => dieuHuong('/ekyc/ho-so') },
            { label: tieuDe },
          ]}
          titleIcon={<IdcardOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('ekyc.chiTietMo')}
          actions={
            <>
              <Badge variant={tongTrangThai.variant}>{t(tongTrangThai.khoa)}</Badge>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => {
                  void q.refetch();
                  void nhatKy.refetch();
                }}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
            </>
          }
        />

        {/*
         * `07` §22.10 — phiên đứng lại vì MÁY CHỦ hỏng. Người duyệt phải đọc ra ngay rằng đây
         * không phải dấu hiệu về người nộp hồ sơ: chưa phép kiểm nào chạy xong.
         */}
        {p.systemError && (
          <Dai
            tone="danger"
            tieuDe={t('ekyc.loiMayChu')}
            than={t('ekyc.loiMayChuMo')}
            phu={p.errorCode ?? undefined}
          />
        )}

        {/* `07` §23.1 — banner THƯỜNG TRÚ, không phải toast. */}
        {canhBaoSo && <Dai tone="warning" tieuDe={t('ekyc.chuaVaoSo')} than={t('ekyc.chuaVaoSoMo')} />}

        <Bento>
          {/* ── Năm hạng mục ── */}
          <Tile span="lg:col-span-7">
            <TileHeader icon={<SafetyCertificateOutlined />} tone="primary" title={t('ekyc.hangMuc')} />
            <TileBody>
              <ul className="space-y-3">
                {THU_TU_HANG_MUC.map((ma) => (
                  <DongHangMuc key={ma} ma={ma} ket={p.checks[ma]} />
                ))}
              </ul>
            </TileBody>
          </Tile>

          {/* ── Căn cứ quyết định ── */}
          <Tile span="lg:col-span-5">
            <TileHeader icon={<FileSearchOutlined />} tone="violet" title={t('ekyc.canCu')} />
            <TileBody>
              <CanCu phien={p} />
            </TileBody>
          </Tile>
        </Bento>

        {/*
         * Cả hai ô dưới **ẩn hẳn** khi thiếu `EKYC_REVIEW.READ` — không hiện khung rỗng.
         *
         * `EKYC_REVIEW` là module riêng (`06` §1.8): cùng một trang, người được xem hồ sơ và người
         * được xem ảnh mặt thật là hai vai khác nhau. Để lại khung trống kèm tiêu đề "Ảnh đã nộp"
         * là nói với người duyệt rằng hồ sơ **không có ảnh** — trong khi ảnh có, chỉ là họ không
         * được xem. Thiếu quyền thì ẩn mục, không hiện mờ (ADR-UI-0005).
         */}
        {quyen.thamDinhDoc && (
        <Bento>
          {/* ── Dữ liệu giấy tờ: mở theo yêu cầu ── */}
          <Tile span="lg:col-span-7">
            <TileHeader icon={<IdcardOutlined />} tone="teal" title={t('ekyc.duLieuGiayTo')} />
            <TileBody>
              {!giayTo.daMo ? (
                <div className="space-y-2">
                  <p className="text-xs leading-relaxed text-content-secondary">{t('ekyc.moDuLieuMo')}</p>
                  <Button variant="outline" size="sm" icon={<EyeOutlined />} onClick={giayTo.mo}>
                    {t('ekyc.moDuLieu')}
                  </Button>
                </div>
              ) : giayTo.dangTai ? (
                <Skeleton className="h-40 w-full" />
              ) : giayTo.loi ? (
                <p className="text-sm text-danger-700">{dichLoi(giayTo.loi)}</p>
              ) : !giayTo.duLieu ? (
                /*
                 * Không có dữ liệu mang HAI nghĩa khác nhau, và nói nhầm là một khẳng định sai với người duyệt:
                 * dữ liệu đã bị xoá theo yêu cầu (phiên `DELETED`), hay chưa từng được bóc ra (bước đọc giấy tờ
                 * chưa chạy). Bản đầu nói "đã xoá" cho cả hai — gặp thật khi chạy thử ngày 26/10/2026.
                 */
                <p className="text-sm text-content-secondary">
                  {p.status === 'DELETED' ? t('ekyc.duLieuDaXoa') : t('ekyc.chuaBocTach')}
                </p>
              ) : (
                <BangGiayTo duLieu={giayTo.duLieu} />
              )}
            </TileBody>
          </Tile>

          {/* ── Ảnh: mở theo yêu cầu ── */}
          <Tile span="lg:col-span-5">
            <TileHeader icon={<PictureOutlined />} tone="orange" title={t('ekyc.anhHoSo')} />
            <TileBody>
                <div className="space-y-3">
                  <p className="text-xs leading-relaxed text-content-secondary">{t('ekyc.moAnhMo')}</p>
                  {VAI_TRO_ANH.map((vaiTro) => {
                    const ten = t(TEN_VAI_TRO_ANH[vaiTro]);
                    const url = anh.anh[vaiTro];
                    const loi = anh.loi[vaiTro];
                    return (
                      <div key={vaiTro}>
                        {url ? (
                          <figure className="space-y-1">
                            <img
                              src={url}
                              alt={ten}
                              className="w-full rounded border border-default text-content-muted"
                            />
                            <figcaption className="text-2xs capitalize text-content-muted">{ten}</figcaption>
                          </figure>
                        ) : loi ? (
                          <p className="text-xs text-danger-700">{t('ekyc.anhLoi')}</p>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<EyeOutlined />}
                            disabled={anh.dangTai === vaiTro}
                            onClick={() => anh.mo(vaiTro)}
                          >
                            {t('ekyc.moAnh', { ten })}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
            </TileBody>
          </Tile>
        </Bento>
        )}

        {/*
         * ── Thẩm định tay ──
         * "Ra quyết định" chỉ hiện khi hồ sơ đang chờ người VÀ có `EKYC_REVIEW.UPDATE` — thiếu quyền thì ẩn, không hiện
         * mờ (ADR-UI-0005). Kết quả lần quyết gần nhất thì ai xem được hồ sơ cũng thấy: đó là câu trả lời cho "vì sao
         * hồ sơ này ở trạng thái này".
         */}
        {((p.status === 'MANUAL_REVIEW' && quyen.thamDinhGhi) || p.review) && (
          <Bento>
            {p.status === 'MANUAL_REVIEW' && quyen.thamDinhGhi && (
              <Tile span={p.review ? 'lg:col-span-7' : 'col-span-12'}>
                <TileHeader icon={<AuditOutlined />} tone="primary" title={t('ekyc.thamDinh.raQuyetDinh')} />
                <TileBody>
                  <KhoiRaQuyetDinh key={p.id} phienId={p.id} />
                </TileBody>
              </Tile>
            )}
            {p.review && (
              <Tile span={p.status === 'MANUAL_REVIEW' && quyen.thamDinhGhi ? 'lg:col-span-5' : 'col-span-12'}>
                <TileHeader icon={<FileDoneOutlined />} tone="violet" title={t('ekyc.thamDinh.ketQuaGanNhat')} />
                <TileBody>
                  <KetQuaThamDinhView review={p.review} daXoa={p.status === 'DELETED'} />
                </TileBody>
              </Tile>
            )}
          </Bento>
        )}

        {/* ── Nhật ký ── */}
        <Bento>
          <Tile span="col-span-12">
            <TileHeader icon={<FileSearchOutlined />} tone="neutral" title={t('ekyc.nhatKy')} />
            <TileBody>
              <BangNhatKy
                dong={nhatKy.data ?? []}
                dangTai={nhatKy.isLoading}
                loi={nhatKy.isError ? t('ekyc.loiNhatKy', { loi: dichLoi(nhatKy.error) }) : null}
              />
            </TileBody>
          </Tile>
        </Bento>
      </div>
    </div>
  );
}

/** Dải cảnh báo thường trú ở đầu màn. */
function Dai({
  tone,
  tieuDe,
  than,
  phu,
}: {
  tone: 'danger' | 'warning';
  tieuDe: string;
  than: string;
  phu?: string;
}) {
  const vien = tone === 'danger' ? 'border-danger-200 bg-danger-50' : 'border-warning-200 bg-warning-50';
  const chu = tone === 'danger' ? 'text-danger-700' : 'text-warning-700';
  const icon = tone === 'danger' ? 'text-danger-600' : 'text-warning-600';
  return (
    <div role="alert" className={`flex gap-3 rounded-lg border p-3 ${vien}`}>
      <WarningOutlined className={`mt-0.5 shrink-0 ${icon}`} />
      <div className="min-w-0">
        <p className={`text-sm font-medium ${chu}`}>{tieuDe}</p>
        <p className={`mt-0.5 text-xs leading-relaxed ${chu}`}>{than}</p>
        {phu && <p className="mt-1 font-mono text-2xs text-content-muted">{phu}</p>}
      </div>
    </div>
  );
}

/**
 * Một hạng mục.
 *
 * Điểm chỉ hiện khi **đo được**. `SKIPPED` và `ERROR` mang `score = 0` nhưng cả hai nói "chưa có
 * câu trả lời" — hiện "0,00" ở đó là một lời nói dối im lặng về người đang bị thẩm định.
 */
function DongHangMuc({ ma, ket }: { ma: MaHangMuc; ket: KetQuaHangMuc }) {
  const { t, locale } = useT();
  const tong = KET_LUAN_HANG_MUC[ket.result];
  const doDuoc = coDiem(ket);
  const cach = khoangCachNguong(ket);

  return (
    <li className="rounded border border-default p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content-primary">{t(TEN_HANG_MUC[ma])}</p>
          <p className="text-2xs leading-relaxed text-content-muted">{t(MO_HANG_MUC[ma])}</p>
        </div>
        <Badge variant={tong.variant}>{t(tong.khoa)}</Badge>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-content-secondary">
        {doDuoc ? (
          <>
            {/*
             * Điểm chỉ hiện khi có ngưỡng đi kèm. "0,52" một mình không nói gì; "0,52 so với
             * ngưỡng 0,72" nói được còn cách bao xa (`03` §7). Hạng mục `logic` là quy tắc thuần,
             * không có thang đo — điểm 0/1 của nó chỉ lặp lại kết luận đã nằm ngay trên, và một
             * con "0" trần cạnh chữ "Không đạt" đọc như một phép đo đã chạy ra số không.
             */}
            {ket.threshold !== null && (
              <>
                <span className="tabular-nums">{soVN(Math.round(ket.score * 100) / 100, locale)}</span>
                <span>{t('ekyc.nguongLa', { so: soVN(Math.round(ket.threshold * 100) / 100, locale) })}</span>
              </>
            )}
            {cach !== null && (
              <span className={cach >= 0 ? 'text-success-700' : 'text-danger-700'}>
                {cach >= 0
                  ? t('ekyc.vuotNguong', { so: soVN(Math.round(Math.abs(cach) * 100) / 100, locale) })
                  : t('ekyc.duoiNguong', { so: soVN(Math.round(Math.abs(cach) * 100) / 100, locale) })}
              </span>
            )}
          </>
        ) : (
          <span title={t('ekyc.khongCoTinHieuMo')}>{t('ekyc.khongCoTinHieu')}</span>
        )}
        {ket.modelVersion && <span className="font-mono">{t('ekyc.phienBanModel', { ten: ket.modelVersion })}</span>}
      </div>

      {ket.issues.length > 0 && (
        <ul className="mt-2 space-y-1">
          {ket.issues.map((v) => {
            const muc = MUC_VAN_DE[v.severity];
            return (
              <li key={v.code} className="flex flex-wrap items-start gap-2">
                <Badge variant={muc.variant}>{t(muc.khoa)}</Badge>
                {/* Mã giữ nguyên — nó là hợp đồng; câu tiếng Việt là bổ sung (`07` §22.9). */}
                <span className="font-mono text-2xs text-content-muted">{v.code}</span>
                <HanMucHoacCauGoc cau={v.message} bienThe="o" className="flex-1 basis-40">
                  <span className="min-w-0 flex-1 text-2xs leading-relaxed text-content-secondary">{v.message}</span>
                </HanMucHoacCauGoc>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

/** Căn cứ quyết định: điểm, mã lý do, luật phủ quyết và chín yếu tố rủi ro. */
function CanCu({ phien }: { phien: PhienChiTiet }) {
  const { t, locale } = useT();
  const qd = phien.decisionDetail;

  /*
   * `01` §4.6 — đường hết hạn mức AI cố ý KHÔNG đưa ra quyết định: một quyết định là phát biểu về
   * một con người, mà ở đây chưa đọc được giấy tờ nào. Màn phải nói đúng điều đó, không hiện 0.
   */
  if (!qd) {
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium text-content-primary">{t('ekyc.chuaCoQuyetDinh')}</p>
        <p className="text-xs leading-relaxed text-content-secondary">{t('ekyc.chuaCoQuyetDinhMo')}</p>
      </div>
    );
  }

  const tongQd = QUYET_DINH[qd.status];
  const tongMuc = MUC_RUI_RO[qd.riskLevel];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={tongQd.variant}>{t(tongQd.khoa)}</Badge>
        <Badge variant={tongMuc.variant}>{t(tongMuc.khoa)}</Badge>
        <span className="text-xs tabular-nums text-content-secondary">
          {t('ekyc.diemRuiRo')}: {soVN(Math.round(qd.riskScore * 10000) / 10000, locale)}
        </span>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-content-primary">{t('ekyc.maLyDo')}</p>
        {qd.reasonCodes.length === 0 ? (
          <p className="text-2xs text-content-muted">{KHONG_CO}</p>
        ) : (
          <ul className="flex flex-wrap gap-1">
            {qd.reasonCodes.map((ma) => (
              <li key={ma}>
                <Badge variant="neutral">
                  <span className="font-mono text-2xs">{ma}</span>
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-content-primary">{t('ekyc.luatPhuQuyet')}</p>
        {qd.hardRules.length === 0 ? (
          <p className="text-2xs text-content-muted">{t('ekyc.luatPhuQuyetRong')}</p>
        ) : (
          /*
           * `05` §8.12 — `hardRules` và `reasonCodes` KHÔNG song ánh: `HR-E02` đóng góp mã lý do
           * rỗng, nên nó có ở đây mà không có mã tương ứng ở trên. Hai danh sách vẽ riêng.
           */
          <ul className="flex flex-wrap gap-1">
            {qd.hardRules.map((ma) => (
              <li key={ma}>
                <Badge variant="warning">
                  <span className="font-mono text-2xs">{ma}</span>
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-content-primary">{t('ekyc.yeuToRuiRo')}</p>
        <ul className="space-y-1">
          {THU_TU_YEU_TO.map((yt) => {
            const giaTri = qd.riskFactors[yt];
            return (
              <li key={yt} className="flex items-center gap-2 text-2xs">
                <span className="w-44 shrink-0 truncate text-content-secondary">{t(TEN_YEU_TO[yt])}</span>
                {giaTri === null ? (
                  /*
                   * `null` = KHÔNG CÓ TÍN HIỆU, khác hẳn 0 = đo ra 0 (`05` §8.2). Đây là thứ cho
                   * đối tác và cơ quan quản lý kiểm lại được phép tính, nên nó phải đọc ra được.
                   */
                  <span className="text-content-muted" title={t('ekyc.khongCoTinHieuMo')}>
                    {t('ekyc.khongCoTinHieu')}
                  </span>
                ) : (
                  <>
                    <span className="h-1.5 min-w-0 flex-1 rounded-full bg-subtle">
                      <span
                        className="block h-1.5 rounded-full bg-primary-500"
                        style={{ width: `${Math.max(2, giaTri * 100)}%` }}
                      />
                    </span>
                    <span className="w-10 shrink-0 text-right tabular-nums text-content-secondary">
                      {soVN(Math.round(giaTri * 100) / 100, locale)}
                    </span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-content-secondary">
        <span className="font-mono">
          {t('ekyc.chinhSach')}: {qd.policyId}
        </span>
        {phien.processingMs !== null && (
          <span>
            {t('ekyc.thoiGian')}: {soVN(Math.round(phien.processingMs / 100) / 10, locale)}s
          </span>
        )}
        <span>
          {t('ekyc.soLanChup')}:{' '}
          {phien.selfieRetryRemaining > 0
            ? t('ekyc.soLanChupCon', { da: phien.selfieAttempts, con: phien.selfieRetryRemaining })
            : t('ekyc.soLanChupHet', { da: phien.selfieAttempts })}
        </span>
      </div>
    </div>
  );
}

/** Bảng dữ liệu giấy tờ: bản máy bóc ra và bản khách sửa nằm cạnh nhau. */
function BangGiayTo({ duLieu }: { duLieu: ThongTinGiayTo }) {
  const { t, locale } = useT();

  const truong = [
    'idNumber',
    'oldIdNumber',
    'fullName',
    'dob',
    'gender',
    'issueDate',
    'expiryDate',
    'issuePlace',
    'residence',
    'nationality',
    'mrz',
  ] as const;

  const tinCay = duLieu.confidence['*'];

  return (
    <div className="space-y-3">
      <dl className="space-y-2">
        {truong.map((ten) => {
          const may = duLieu[ten];
          const sua = duLieu.edited?.[ten];
          if (may === null && sua === undefined) return null;
          const hienMay = ten === 'gender' && may ? t(`ekyc.gioiTinh.${may as 'NAM' | 'NU' | 'KHAC'}`) : may;
          return (
            <div key={ten} className="flex flex-wrap gap-x-3 gap-y-0.5">
              <dt className="w-36 shrink-0 text-2xs text-content-muted">{t(`ekyc.truong.${ten}`)}</dt>
              <dd className="min-w-0 flex-1 text-xs text-content-primary">
                <span className="break-words">{hienMay ?? KHONG_CO}</span>
                {/*
                 * `03` §8 — sửa của khách lưu RIÊNG, không ghi đè bản máy bóc ra: khi tranh chấp,
                 * câu hỏi luôn là "máy đọc ra gì". Nên hai bản phải cùng hiện.
                 */}
                {sua !== undefined && sua !== may && (
                  <span className="ml-2 text-2xs text-warning-700">
                    {t('ekyc.khachSua')}: {sua}
                  </span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>

      {tinCay !== undefined && (
        <p className="text-2xs text-content-muted">
          {t('ekyc.doTinCay', { so: soVN(Math.round(tinCay * 100) / 100, locale) })} ·{' '}
          {t('ekyc.doTinCayToanTaiLieu')}
        </p>
      )}
    </div>
  );
}

function BangNhatKy({ dong, dangTai, loi }: { dong: DongNhatKy[]; dangTai: boolean; loi: string | null }) {
  const { t } = useT();

  if (dangTai) return <Skeleton className="h-24 w-full" />;
  if (loi) return <p className="text-sm text-danger-700">{loi}</p>;
  if (dong.length === 0) return <p className="text-sm text-content-secondary">{t('ekyc.nhatKyRong')}</p>;

  const cot = [
    {
      title: t('ekyc.nhatKyCotLuc'),
      dataIndex: 'at',
      key: 'at',
      render: (_: unknown, d: DongNhatKy) => (
        <span className="whitespace-nowrap text-content-secondary">{ngayGioVN(d.at)}</span>
      ),
    },
    {
      title: t('ekyc.nhatKyCotHanhDong'),
      dataIndex: 'action',
      key: 'action',
      render: (_: unknown, d: DongNhatKy) => <span className="font-mono text-2xs">{d.action}</span>,
    },
    {
      title: t('ekyc.nhatKyCotNguoi'),
      dataIndex: 'actor',
      key: 'actor',
      render: (_: unknown, d: DongNhatKy) => (
        <div className="min-w-0">
          <p className="truncate text-content-primary">{d.actor}</p>
          <p className="truncate text-2xs text-content-muted">{d.ip}</p>
        </div>
      ),
    },
    {
      title: t('chung.trangThai'),
      dataIndex: 'hash',
      key: 'hash',
      render: (_: unknown, d: DongNhatKy) =>
        /*
         * `03` §14 — nối chuỗi là việc của JOB (cron 5 phút), không phải lúc ghi. `hash === null`
         * nghĩa là CHƯA NỐI, không phải chuỗi hỏng: dòng vẫn đọc được, chỉ chưa chống được sửa lén.
         */
        d.hash === null ? (
          <Badge variant="neutral">
            <span title={t('ekyc.nhatKyChuaNoiMo')}>{t('ekyc.nhatKyChuaNoi')}</span>
          </Badge>
        ) : (
          <span className="font-mono text-2xs text-content-muted">{d.hash.slice(0, 8)}</span>
        ),
    },
  ];

  return <DataTable<DongNhatKy> caption={t('ekyc.bangNhatKy')} columns={cot} dataSource={dong} pagination={false} />;
}
